import { useEffect, useState } from "react";
import { Search } from "lucide-react";
import { http } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { RevealLines, FadeUp } from "@/components/Reveal";
import { ThoughtCard } from "@/components/Thoughts";
import { ThoughtForm, MOODS } from "@/components/ThoughtForm";

const Filters = ({ filter, setFilter, counts }) => (
  <div className="flex flex-wrap gap-2" data-testid="thoughts-page-filters">
    {["All", ...MOODS].map((m) => (
      <button key={m} data-testid={`thoughts-page-filter-${m.toLowerCase()}`} onClick={() => setFilter(m)}
        className={`rounded-full px-4 py-1.5 text-sm border transition-colors ${filter === m ? "bg-purple-100 text-[#0e0616] border-purple-100" : "border-purple-200/40 bg-[#0e0616]/40 hover:border-purple-100"}`}>
        {m} <span className="opacity-70">{counts[m] ?? 0}</span>
      </button>
    ))}
  </div>
);

export default function ThoughtsPage() {
  const { user } = useAuth();
  const [thoughts, setThoughts] = useState([]);
  const [filter, setFilter] = useState("All");
  const [q, setQ] = useState("");

  useEffect(() => {
    window.scrollTo(0, 0);
    http.get("/thoughts")
      .then((r) => setThoughts(Array.isArray(r.data) ? r.data : []))
      .catch(() => setThoughts([]));
  }, []);

  const remove = async (id) => {
    try { await http.delete(`/thoughts/${id}`); setThoughts((p) => p.filter((t) => t.id !== id)); } catch { /* ignore */ }
  };

  const counts = thoughts.reduce((c, t) => ({ ...c, [t.mood]: (c[t.mood] ?? 0) + 1 }), { All: thoughts.length });
  const term = q.trim().toLowerCase();
  const shown = thoughts.filter((t) => (filter === "All" || t.mood === filter) &&
    (!term || t.text?.toLowerCase().includes(term) || t.author_name?.toLowerCase().includes(term)));

  return (
    <main className="relative min-h-screen pt-32 pb-24 overflow-hidden" data-testid="thoughts-page">
      <div className="fixed inset-0 -z-0">
        <img src="/img/galaxy.webp" alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[#0e0616]/70" />
      </div>
      <div className="relative max-w-[1400px] mx-auto px-5 md:px-10">
        <div className="grid lg:grid-cols-12 gap-10 mb-14">
          <div className="lg:col-span-7">
            <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-purple-200 mb-6">The Thought Wall</p>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.92] tracking-tight" data-testid="thoughts-page-title">
              <RevealLines lines={[<>Every whisper</>, <em className="text-amber-200">left in the hollow.</em>]} />
            </h1>
            <FadeUp className="mt-8 max-w-md text-purple-50/90 leading-relaxed">
              Read what others have shared: dreams, secrets, wishes and memories. Be kind; everyone here is a little lost.
            </FadeUp>
          </div>
          <div className="lg:col-span-5 lg:pt-6"><ThoughtForm onPosted={(t) => setThoughts((p) => [t, ...p])} /></div>
        </div>
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <Filters filter={filter} setFilter={setFilter} counts={counts} />
          <label className="flex items-center gap-2 rounded-full border border-purple-200/30 bg-[#0e0616]/60 backdrop-blur px-4 py-2.5 md:w-80">
            <Search className="w-4 h-4 text-purple-200" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search thoughts or names" data-testid="thoughts-page-search-input"
              className="bg-transparent outline-none text-sm w-full placeholder:text-purple-200/70" />
          </label>
        </div>
        <div className="columns-1 md:columns-2 lg:columns-3 gap-5" data-testid="thoughts-page-grid">
          {shown.map((t, i) => <ThoughtCard key={t.id} t={t} i={i} mine={user?.user_id === t.user_id} onDelete={remove} />)}
        </div>
        {shown.length === 0 && (
          <p className="font-display italic text-2xl text-purple-100 text-center py-16" data-testid="thoughts-page-empty">
            {thoughts.length === 0 ? "The hollow is quiet… be the first to whisper." : "No thoughts match that search."}
          </p>
        )}
      </div>
    </main>
  );
}
